/**
 * E06-F01-T06 — Purpose toggle + Grade-scope toggle section of the Intake form.
 *
 *   - Purpose toggle (Sourcing | Market Mapping) with a dynamic #purpose-hint
 *     (purposeHint): Market Mapping = exhaustive "find ALL companies" (higher
 *     target count, wider discovery fan-out); Sourcing = ranked long-list.
 *   - Grade scope toggle (A | A+B | A+B+C): applyGradeScope shows/hides the
 *     Grade B card (scope !== 'A') and Grade C card (scope === 'A+B+C'). The
 *     Grade A card is always shown. Each visible card carries an editable
 *     definition textarea (gradeDefinitions.a/b/c).
 *
 * Controlled — `purpose` / `gradeScope` / `gradeDefinitions` come from the parent
 * criteria; every change writes back through the callbacks. Pure rules live in
 * intakeModel.ts (purposeHint / gradeCardVisible / option lists).
 */
import type { JSX } from 'react';
import { SegmentedToggle } from '../ui/SegmentedToggle.js';
import type { GradeScope, IntakeResearchPurpose, GradeDefinitions } from '@eer/shared';
import {
  GRADE_SCOPE_OPTIONS,
  PURPOSE_OPTIONS,
  gradeCardVisible,
  purposeHint,
} from './intakeModel.js';

export interface PurposeGradeScopeSectionProps {
  readonly purpose: IntakeResearchPurpose;
  readonly onPurposeChange: (p: IntakeResearchPurpose) => void;
  readonly gradeScope: GradeScope;
  readonly onGradeScopeChange: (s: GradeScope) => void;
  readonly gradeDefinitions: GradeDefinitions;
  readonly onGradeDefinitionsChange: (g: GradeDefinitions) => void;
}

/** One grade-definition card (label + editable definition). */
function GradeCard({
  grade,
  value,
  onChange,
}: {
  grade: 'a' | 'b' | 'c';
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div
      data-testid={`grade-card-${grade}`}
      className="rounded-lg border border-[var(--color-border)] p-3 bg-white flex flex-col gap-1"
    >
      <span className="text-sm font-semibold text-[var(--color-text-strong)]">
        Grade {grade.toUpperCase()}
      </span>
      <textarea
        data-testid={`grade-def-${grade}`}
        className="border border-[var(--color-border)] rounded-md p-2 text-sm min-h-[56px] outline-none focus:border-[var(--color-accent)]"
        placeholder={`Definition for Grade ${grade.toUpperCase()}…`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function PurposeGradeScopeSection({
  purpose,
  onPurposeChange,
  gradeScope,
  onGradeScopeChange,
  gradeDefinitions,
  onGradeDefinitionsChange,
}: PurposeGradeScopeSectionProps): JSX.Element {
  const showB = gradeCardVisible(gradeScope, 'b');
  const showC = gradeCardVisible(gradeScope, 'c');

  return (
    <section data-testid="intake-purpose-grades" className="flex flex-col gap-4">
      {/* Purpose */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-[var(--color-text-strong)]">Purpose</span>
        <SegmentedToggle<IntakeResearchPurpose>
          options={PURPOSE_OPTIONS}
          value={purpose}
          onChange={onPurposeChange}
          ariaLabel="Research purpose"
          testId="intake-purpose"
        />
        <p data-testid="purpose-hint" className="text-xs text-[var(--color-text-muted)]">
          {purposeHint(purpose)}
        </p>
      </div>

      {/* Grade scope */}
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-[var(--color-text-strong)]">Grade scope</span>
        <SegmentedToggle<GradeScope>
          options={GRADE_SCOPE_OPTIONS}
          value={gradeScope}
          onChange={onGradeScopeChange}
          ariaLabel="Grade scope"
          testId="intake-grade-scope"
        />
      </div>

      {/* Grade cards (A always; B/C by scope) */}
      <div data-testid="grade-cards" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <GradeCard
          grade="a"
          value={gradeDefinitions.a}
          onChange={(v) => onGradeDefinitionsChange({ ...gradeDefinitions, a: v })}
        />
        {showB && (
          <GradeCard
            grade="b"
            value={gradeDefinitions.b}
            onChange={(v) => onGradeDefinitionsChange({ ...gradeDefinitions, b: v })}
          />
        )}
        {showC && (
          <GradeCard
            grade="c"
            value={gradeDefinitions.c}
            onChange={(v) => onGradeDefinitionsChange({ ...gradeDefinitions, c: v })}
          />
        )}
      </div>
    </section>
  );
}
